import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { ExpandMore, Inventory } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import { Avatar, Box, Chip, Badge } from '@mui/material';
import moment from 'moment';
import { useState, useEffect } from 'react';
import './style.css';

export interface AccordionProps {
    data: any[];
    handleRedirect: (itemId: string) => void;
    isFiltered?: boolean;
}

const AccordionComponent = ({ 
    data, 
    handleRedirect, 
    isFiltered = false 
}: AccordionProps) => {
    // Estado para controlar quais painéis estão expandidos
    const [expandedPanels, setExpandedPanels] = useState<{[key: string]: boolean}>({});
    
    // Efeito para expandir todos os painéis quando há filtro
    useEffect(() => {
        if (isFiltered && data.length > 0) {
            // Criar um objeto com todos os painéis expandidos
            const newExpandedState: {[key: string]: boolean} = {};
            data.forEach((item, index) => {
                newExpandedState[`panel-${index}`] = true;
            });
            setExpandedPanels(newExpandedState);
        } else if (!isFiltered) {
            // Fechar todos os painéis quando o filtro é removido
            setExpandedPanels({});
        }
    }, [isFiltered, data]);
    
    // Função para controlar a expansão/contração de um painel específico
    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanels({
            ...expandedPanels,
            [panel]: isExpanded
        });
    };
    
    const clickToNewPage = (itemId: string) => {
        handleRedirect(itemId);
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');

    // Se não houver dados, mostrar mensagem de que nenhum projeto foi encontrado
    if (!data || data.length === 0) {
        return (
            <div className="no-results">
                <Typography variant="body1" sx={{ textAlign: 'center', padding: '20px' }}>
                    Nenhum projeto encontrado.
                </Typography>
            </div>
        );
    }

    return (
        <div className="modern-accordion-container">
            {data.map((item, index) => (
                <div key={index} className="researcher-section">
                    <Accordion 
                        className='modern-accordion' 
                        expanded={expandedPanels[`panel-${index}`] || false}
                        onChange={handleAccordionChange(`panel-${index}`)}
                        elevation={0}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            aria-controls={`panel-${index}-content`}
                            id={`panel-${index}-header`}
                            className='modern-accordion-summary'
                        >
                            <Box className="researcher-header">
                                <Box className="researcher-info">
                                    <Badge 
                                        badgeContent={item.projects.length} 
                                        color="error"
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                backgroundColor: '#10b981',
                                                color: 'white',
                                                fontWeight: 600,
                                                fontSize: '12px',
                                                minWidth: '20px',
                                                height: '20px',
                                                top: 8,
                                                right: 8
                                            }
                                        }}
                                    >
                                        <Avatar className="researcher-avatar">
                                            <Inventory />
                                        </Avatar>
                                    </Badge>
                                    <Box className="researcher-details">
                                        <Typography variant="h6" className="researcher-name">
                                            {item.principalInvestigator?.name}
                                        </Typography>
                                        <Typography variant="body2" className="researcher-projects">
                                            {item.projects.length} projetos
                                        </Typography>
                                        {user.name?.toLowerCase() === 'cauane' && (
                                            <Typography variant="body2" className="researcher-company">
                                                Empresa: {item?.company?.name}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                <Chip 
                                    label={item.projects.length} 
                                    className="projects-count-chip"
                                    size="small"
                                    sx={{ display: 'none' }}
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails className='modern-accordion-details'>
                            <div className="projects-grid">
                                {item.projects.map((project: any, projectIndex: number) => (
                                    <div 
                                        key={projectIndex} 
                                        className='project-card' 
                                        onClick={() => clickToNewPage(project.id)}
                                    >
                                        <div className="project-header">
                                            <Typography variant="body1" className="project-name">
                                                <strong>Nome do projeto:</strong> {project?.name}
                                            </Typography>
                                        </div>
                                        <div className="project-details">
                                            <div className="project-detail-item">
                                                <strong>Financiador:</strong> {project.financier?.name}
                                            </div>
                                            <div className="project-detail-item">
                                                <strong>Data Inicial:</strong> {moment(project.initialDate).format('DD/MM/YYYY')}
                                            </div>
                                            <div className="project-detail-item">
                                                <strong>Data Final:</strong> {moment(project.finalDate).format('DD/MM/YYYY')}
                                            </div>
                                            <div className="project-detail-item">
                                                <strong>Duração:</strong> {project.duration}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </AccordionDetails>
                    </Accordion>        
                </div>
            ))}
        </div>
    );
}

export default AccordionComponent;